<template>
  <v-app :style="cssVarsStyle">
    <!-- Loading State with White Background and Logo -->
    <div v-if="loading" class="menu-loading-screen">
      <img src="@/assets/logo-1x.png" alt="Logo" class="menu-loading-logo">
      <div class="menu-loading-spinner"></div>
    </div>

    <!-- Error State -->
    <div v-if="error && !loading" class="error-container">
      <v-container class="fill-height">
        <v-row align="center" justify="center">
          <v-col cols="12" sm="8" md="6" class="text-center">
            <v-icon size="120" color="grey lighten-1">mdi-alert-circle-outline</v-icon>
            <h2 class="mt-4 mb-2">{{ error }}</h2>
            <p class="text-body-1 grey--text">Molimo kontaktirajte osoblje za pomoć.</p>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <!-- Main Menu Content -->
    <div v-else-if="!loading">
      <!-- App Bar with Company Branding -->
      <v-app-bar
        fixed
        color="white"
        flat
        class="app-bar-custom"
        :class="{ 'app-bar-hidden': !showAppBar }"
        @click="scrollToTop"
      >
        <div class="app-bar-logo-wrapper">
          <v-img
            v-if="companyLogo"
            :src="companyLogo"
            max-width="40"
            max-height="40"
            contain
            class="app-bar-logo"
          ></v-img>
          <v-icon v-else size="32" color="primary">mdi-silverware-fork-knife</v-icon>
        </div>

        <v-toolbar-title class="app-bar-title">
          {{ displayRestaurantName }}
        </v-toolbar-title>

        <v-spacer></v-spacer>

        <!-- Cart Icon (if ordering enabled) -->
        <v-btn
          v-if="menuSettings.enable_ordering"
          icon
          @click.stop="openCart"
          aria-label="Otvori košaricu"
          color="primary"
        >
          <v-badge
            :content="cartItemCount"
            :value="cartItemCount > 0"
            color="red"
            overlap
          >
            <v-icon>mdi-cart</v-icon>
          </v-badge>
        </v-btn>
      </v-app-bar>

      <!-- Main Content -->
      <v-main class="menu-main">
        <!-- Hero Section -->
        <div
          class="hero-section"
          ref="heroSection"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="handleTouchEnd"
        >
          <!-- Hero Image - Fixed at top -->
          <div class="hero-image-container-full">
            <div class="hero-image-wrapper-full">
              <v-img
                :src="heroImageUrl"
                class="hero-image"
                :style="{
                  transform: `scale(${1 + (pullOffset / 2000)})`,
                  transition: isReleasing ? 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none'
                }"
              >
              </v-img>
            </div>
          </div>

          <!-- Content that moves down with pull -->
          <div
            class="hero-content-wrapper"
            :style="heroWrapperStyle"
          >
            <!-- Background overlay that extends to bottom -->
            <div class="hero-background-overlay" :style="heroOverlayBackgroundStyle"></div>

            <!-- Restaurant Info Overlay on Image -->
            <div class="hero-content-overlay">
              <div :style="heroContentStyle">
                <!-- Logo -->
                <div v-if="showLogo" class="hero-logo-wrapper">
                  <v-img
                    v-if="companyLogo"
                    :src="companyLogo"
                    max-width="80"
                    max-height="80"
                    contain
                    class="hero-logo"
                  ></v-img>
                  <v-icon v-else size="80" color="primary" class="hero-logo-icon">mdi-silverware-fork-knife</v-icon>
                </div>

                <!-- Restaurant Name -->
                <h1 class="hero-title" :style="heroTextStyle">
                  {{ displayRestaurantName }}
                </h1>

                <!-- Description -->
                <p v-if="heroDescription" class="hero-description" :style="heroDescriptionStyle">
                  {{ heroDescription }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Horizontal Category Tabs with Sticky Button - Outside wrapper for sticky positioning -->
        <div class="category-tabs-container" :style="categoryTabsStyle">
          <div class="category-tabs-wrapper">
            <v-tabs
              v-model="selectedCategoryIndex"
              background-color="transparent"
              :color="primaryColor"
              :slider-color="primaryColor"
              class="category-tabs"
            >
              <v-tab
                v-for="(category, index) in filteredCategories"
                :key="category.id"
                class="category-tab"
                @click="scrollToCategory(index)"
              >
                <template v-if="menuSettings.show_images">
                  <v-avatar v-if="category.picture" size="32" class="mr-2">
                    <v-img :src="category.picture"></v-img>
                  </v-avatar>
                  <v-icon v-else small class="mr-2">mdi-food</v-icon>
                </template>
                <span class="category-name-text">{{ category.name }}</span>
              </v-tab>
            </v-tabs>
          </div>

          <!-- Categories Menu Button - Sticky Right -->
          <v-btn
            icon
            class="categories-menu-btn"
            :style="{ backgroundColor: primaryColor }"
            @click="categoriesSheet = true"
          >
            <v-icon color="white">mdi-menu</v-icon>
          </v-btn>
        </div>

        <!-- Items List -->
        <v-container fluid class="pa-0 items-list-container" :style="itemsListStyle">
          <!-- Empty State -->
          <div v-if="filteredCategories.length === 0" class="empty-state pa-8 text-center">
            <v-icon size="80" color="grey lighten-1">mdi-food-off</v-icon>
            <p class="text-h6 mt-4 grey--text">Trenutno nema dostupnih artikala</p>
          </div>

          <!-- All Items Grouped by Category -->
          <div v-else class="items-container">
            <div
              v-for="category in filteredCategories"
              :key="category.id"
              :ref="`category-${category.id}`"
              :data-category-id="category.id"
              class="category-section"
            >
              <!-- Category Header -->
              <div class="category-section-header" :style="categoryHeaderStyle">
                <template v-if="menuSettings.show_images">
                  <v-avatar v-if="category.picture" size="40" class="mr-3">
                    <v-img :src="category.picture"></v-img>
                  </v-avatar>
                  <v-icon v-else size="32" class="mr-3" :color="iconColorStyle">mdi-food</v-icon>
                </template>
                <span class="category-section-title" :style="itemsTextStyle">{{ category.name }}</span>
                <span class="category-item-count" :style="categoryItemCountStyle">{{ getCategoryItemCount(category.id) }}</span>
              </div>

              <!-- Category Items -->
              <div
                v-for="item in getCategoryItems(category.id)"
                :key="item.id"
                :data-item-id="item.id"
                class="item-row"
                :class="{ 'item-clickable': menuSettings.clickable_items }"
                @click="menuSettings.clickable_items ? showItemDetails(item) : null"
              >
              <div class="item-content">
                <!-- Left: Image -->
                <div v-if="menuSettings.show_images" class="item-image-wrapper" :style="itemImageWrapperStyle">
                  <v-img
                    v-if="item.picture"
                    :src="item.picture"
                    class="item-image"
                  >
                  </v-img>
                  <div v-else class="item-image-placeholder" :style="itemImagePlaceholderStyle">
                    <v-icon size="50" :color="iconPlaceholderColorStyle">mdi-silverware-fork-knife</v-icon>
                  </div>
                </div>

                <!-- Right: Info -->
                <div class="item-info">
                  <!-- Top Row: Name and Price -->
                  <div class="item-header">
                    <div class="item-name" :style="itemsTextStyle">{{ item.name }}</div>
                    <div
                      v-if="menuSettings.show_prices && getItemPrice(item)"
                      class="item-price"
                      :style="itemsTextStyle"
                    >
                      {{ formatPrice(getItemPrice(item)) }}
                    </div>
                  </div>

                  <!-- Description -->
                  <div
                    v-if="menuSettings.show_descriptions && item.description"
                    class="item-description"
                  >
                    {{ truncateText(item.description, 100) }}
                  </div>

                  <!-- Allergens -->
                  <div
                    v-if="menuSettings.show_allergens && item.allergens"
                    class="item-allergens"
                  >
                    <v-icon x-small color="#f59e0b">mdi-alert-circle-outline</v-icon>
                    <span>{{ item.allergens }}</span>
                  </div>
                </div>

                <!-- Add Button (Floating) -->
                <v-btn
                  v-if="menuSettings.enable_ordering && getItemPrice(item)"
                  icon
                  small
                  color="primary"
                  class="add-button-floating"
                  @click.stop="addToCart(item)"
                >
                  <v-icon small>mdi-plus</v-icon>
                </v-btn>
              </div>
              <v-divider class="item-divider"></v-divider>
            </div>
            </div>
          </div>
        </v-container>
      </v-main>

      <!-- Floating Call Waiter Button -->
      <v-btn
        v-if="menuSettings.enable_call_waiter"
        fab
        fixed
        bottom
        right
        color="accent"
        large
        @click="callWaiter"
        class="call-waiter-btn"
        aria-label="Pozovi konobara"
      >
        <v-icon>mdi-bell-ring</v-icon>
      </v-btn>

      <!-- Cart Dialog -->
      <v-dialog v-model="cartDialog" max-width="600" scrollable>
        <v-card>
          <v-card-title class="primary white--text">
            <v-icon left color="white">mdi-cart</v-icon>
            Vaša košarica
            <v-spacer></v-spacer>
            <v-btn icon dark @click="cartDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text style="max-height: 400px;" class="pa-0">
            <v-list v-if="cart.length > 0">
              <v-list-item v-for="(cartItem, index) in cart" :key="index">
                <v-list-item-content>
                  <v-list-item-title>{{ cartItem.name }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ formatPrice(getItemPrice(cartItem)) }} x {{ cartItem.quantity }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <div class="d-flex align-center">
                    <v-btn icon small @click="decrementQuantity(index)">
                      <v-icon small>mdi-minus</v-icon>
                    </v-btn>
                    <span class="mx-2">{{ cartItem.quantity }}</span>
                    <v-btn icon small @click="incrementQuantity(index)">
                      <v-icon small>mdi-plus</v-icon>
                    </v-btn>
                    <v-btn icon small color="red" @click="removeFromCart(index)" class="ml-2">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-list-item-action>
              </v-list-item>
            </v-list>
            <div v-else class="pa-8 text-center grey--text">
              <v-icon size="64" color="grey lighten-1">mdi-cart-outline</v-icon>
              <p class="mt-2">Košarica je prazna</p>
            </div>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-text v-if="cart.length > 0">
            <div class="d-flex justify-space-between align-center">
              <span class="text-h6">Ukupno:</span>
              <span class="text-h6 primary--text font-weight-bold">
                {{ formatPrice(cartTotal) }}
              </span>
            </div>
          </v-card-text>

          <v-card-actions v-if="cart.length > 0">
            <v-btn text @click="clearCart" color="error">Obriši sve</v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="submitOrder" :loading="submittingOrder">
              <v-icon left>mdi-check</v-icon>
              Pošalji narudžbu
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Categories Bottom Sheet -->
      <v-bottom-sheet v-model="categoriesSheet" inset>
        <v-card class="categories-sheet-card">
          <v-card-title class="d-flex align-center justify-space-between">
            <span :style="{ color: primaryColor }">Kategorije</span>
            <v-btn icon @click="categoriesSheet = false">
              <v-icon :color="primaryColor">mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-0">
            <v-list>
              <v-list-item
                v-for="(category, index) in filteredCategories"
                :key="category.id"
                @click="selectCategoryFromSheet(index)"
                :class="{ 'v-list-item--active': selectedCategoryIndex === index }"
              >
                <v-list-item-avatar v-if="menuSettings.show_images">
                  <v-img v-if="category.picture" :src="category.picture"></v-img>
                  <v-icon v-else :color="primaryColor">mdi-food</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title :style="{ color: primaryColor }">{{ category.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ getCategoryItemCount(category.id) }} artikala</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-icon v-if="selectedCategoryIndex === index" :color="primaryColor">mdi-check</v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-bottom-sheet>

      <!-- Floating Search Bar -->
      <div class="floating-search-bar">
        <div class="search-container">
          <!-- Search Results Dropdown - Above Input -->
          <v-expand-transition>
            <div v-if="searchExpanded && (searchResults.length > 0 || searchQuery.length >= 3)" class="search-results-wrapper">
              <v-card
                v-if="searchResults.length > 0"
                class="search-results-card"
                elevation="8"
              >
                <v-list dense class="pa-0">
                  <v-list-item
                    v-for="result in searchResults"
                    :key="result.id + result.type"
                    @click="handleSearchResultClick(result)"
                    class="search-result-item"
                  >
                    <!-- Category Result -->
                    <template v-if="result.type === 'category'">
                      <template v-if="menuSettings.show_images">
                        <v-list-item-avatar v-if="result.picture" size="40">
                          <v-img :src="result.picture"></v-img>
                        </v-list-item-avatar>
                        <v-list-item-avatar v-else size="40" :style="{ backgroundColor: `${primaryColorRgb.r}, ${primaryColorRgb.g}, ${primaryColorRgb.b}, 0.1` }">
                          <v-icon small :color="primaryColor">mdi-food</v-icon>
                        </v-list-item-avatar>
                      </template>
                      <v-list-item-content>
                        <v-list-item-title class="search-result-name" :style="{ color: primaryColor }">
                          {{ result.name }}
                        </v-list-item-title>
                        <v-list-item-subtitle class="text-caption">
                          <v-chip x-small :color="primaryColor" text-color="white" class="mr-1">Kategorija</v-chip>
                          {{ getCategoryItemCount(result.id) }} artikala
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-icon small :color="primaryColor">mdi-chevron-right</v-icon>
                      </v-list-item-action>
                    </template>

                    <!-- Item Result -->
                    <template v-else>
                      <template v-if="menuSettings.show_images">
                        <v-list-item-avatar v-if="result.picture" size="40">
                          <v-img :src="result.picture"></v-img>
                        </v-list-item-avatar>
                        <v-list-item-avatar v-else size="40" color="grey lighten-3">
                          <v-icon small color="grey">mdi-silverware-fork-knife</v-icon>
                        </v-list-item-avatar>
                      </template>
                      <v-list-item-content>
                        <v-list-item-title class="search-result-name" :style="{ color: primaryColor }">
                          {{ result.name }}
                        </v-list-item-title>
                        <v-list-item-subtitle v-if="result.description" class="text-caption">
                          {{ result.description.substring(0, 50) }}{{ result.description.length > 50 ? '...' : '' }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action v-if="menuSettings.show_prices && getItemPrice(result)">
                        <span class="search-result-price" :style="{ color: primaryColor }">{{ formatPrice(getItemPrice(result)) }}</span>
                      </v-list-item-action>
                    </template>
                  </v-list-item>
                </v-list>
              </v-card>

              <!-- No Results Message -->
              <v-card
                v-else-if="searchQuery.length >= 3"
                class="search-results-card"
                elevation="8"
              >
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="text-center grey--text">
                      Nema rezultata za "{{ searchQuery }}"
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-card>
            </div>
          </v-expand-transition>

          <!-- Search Input - Fixed at Bottom -->
          <v-expand-transition>
            <v-text-field
              v-if="searchExpanded"
              v-model="searchQuery"
              dense
              outlined
              hide-details
              placeholder="Pretraži..."
              class="search-input"
              autofocus
            >
              <template v-slot:prepend-inner>
                <v-icon small :color="primaryColor">mdi-magnify</v-icon>
              </template>
              <template v-slot:append>
                <v-icon small :color="primaryColor" @click.stop="clearSearch">mdi-close</v-icon>
              </template>
            </v-text-field>
          </v-expand-transition>
        </div>

        <v-btn
          v-if="!searchExpanded"
          fab
          small
          class="search-fab"
          :color="primaryColor"
          @click="expandSearch"
        >
          <v-icon color="white">mdi-magnify</v-icon>
        </v-btn>
      </div>

      <!-- Snackbar for notifications -->
      <v-snackbar
        v-model="snackbar"
        :color="snackbarColor"
        :timeout="3000"
        bottom
      >
        {{ snackbarText }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar = false">
            Zatvori
          </v-btn>
        </template>
      </v-snackbar>

      <!-- Item Details Bottom Sheet -->
      <v-bottom-sheet v-model="itemDetailsDialog" inset>
        <v-card v-if="selectedItem" class="item-details-sheet-card">
          <!-- Item Image -->
          <v-img
            v-if="selectedItem.picture"
            :src="selectedItem.picture"
            height="300"
            class="white--text align-end"
          >
            <div class="item-detail-header">
              <v-card-title class="item-detail-title">{{ selectedItem.name }}</v-card-title>
              <v-btn icon @click="itemDetailsDialog = false" class="close-btn">
                <v-icon color="white">mdi-close</v-icon>
              </v-btn>
            </div>
          </v-img>
          <div v-else class="item-detail-header-no-image">
            <v-card-title>{{ selectedItem.name }}</v-card-title>
            <v-btn icon @click="itemDetailsDialog = false">
              <v-icon :color="primaryColor">mdi-close</v-icon>
            </v-btn>
          </div>

          <v-card-text class="pt-4">
            <!-- Category -->
            <div v-if="getItemCategoryName(selectedItem)" class="mb-3">
              <v-chip small :color="primaryColor" text-color="white">
                {{ getItemCategoryName(selectedItem) }}
              </v-chip>
            </div>

            <!-- Description -->
            <div v-if="selectedItem.description" class="mb-4">
              <div class="text-subtitle-2 mb-1">Opis:</div>
              <div class="text-body-2">{{ selectedItem.description }}</div>
            </div>

            <!-- Allergens -->
            <div v-if="selectedItem.allergens" class="mb-4">
              <div class="text-subtitle-2 mb-1">
                <v-icon small color="#f59e0b">mdi-alert-circle-outline</v-icon>
                Alergeni:
              </div>
              <div class="text-body-2">{{ selectedItem.allergens }}</div>
            </div>

            <!-- Price -->
            <div v-if="getItemPrice(selectedItem)" class="mt-4">
              <v-divider class="mb-3"></v-divider>
              <div class="d-flex justify-space-between align-center">
                <span class="text-h6">Cijena:</span>
                <span class="text-h5" :style="{ color: primaryColor }">
                  {{ formatPrice(getItemPrice(selectedItem)) }}
                </span>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-bottom-sheet>
    </div>
  </v-app>
</template>

<script>
import { firebase, df } from '@/plugins/firebase'

export default {
  name: 'PublicMenuAikasa',
  props: {
    menuData: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    companyId: null,
    companyName: '',
    companyLogo: '',
    locationName: '',
    locationAddress: '',
    menuSettings: {
      show_images: true,
      show_descriptions: true,
      show_allergens: true,
      show_prices: true,
      enable_ordering: false,
      enable_call_waiter: false,
      default_currency: 'EUR',
      hide_inactive_items: true,
      hide_empty_categories: true
    },
    categories: [],
    items: [],
    cart: [],
    cartDialog: false,
    selectedCategoryIndex: 0,
    categoriesSheet: false,
    searchQuery: '',
    searchExpanded: false,
    searchResults: [],
    loading: false,
    error: null,
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success',
    itemDetailsDialog: false,
    selectedItem: null,
    submittingOrder: false,
    scrollTimeout: null,
    showAppBar: false,
    parallaxOffset: 0,
    pullOffset: 0,
    touchStartY: 0,
    isReleasing: false,
    isPulling: false
  }),
  mounted () {
    this.applyMenuData(this.menuData)
    window.addEventListener('scroll', this.handleScroll)
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.handleScroll)
  },
  watch: {
    searchQuery (newVal) {
      if (newVal && newVal.length >= 3) {
        this.filterSearchResults(newVal)
      } else {
        this.searchResults = []
      }
    },
    menuData: {
      handler (newData) {
        if (newData) {
          this.applyMenuData(newData)
        }
      },
      deep: true
    }
  },
  computed: {
    heroImageUrl () {
      // Use custom hero image if provided, otherwise default food image
      return this.menuSettings.hero_image_url ||
        'https://images.unsplash.com/photo-1504674900247-0877df9cc836?ixlib=rb-4.0.3&auto=format&fit=crop&w=1740&q=80'
    },
    displayRestaurantName () {
      return this.menuSettings.custom_restaurant_name || this.locationName || this.companyName || 'Meni'
    },
    heroDescription () {
      return this.menuSettings.hero_description || ''
    },
    heroTextStyle () {
      const style = {}
      if (this.menuSettings.hero_text_color) {
        style.color = this.menuSettings.hero_text_color
      }
      if (this.menuSettings.hero_font_family) {
        style.fontFamily = this.menuSettings.hero_font_family
      }

      // Font size
      const fontSize = this.menuSettings.hero_font_size || 'medium'
      const fontSizeMap = {
        small: { title: '20px', description: '14px' },
        medium: { title: '28px', description: '16px' },
        large: { title: '36px', description: '18px' },
        xlarge: { title: '44px', description: '20px' }
      }
      style.fontSize = fontSizeMap[fontSize]?.title || fontSizeMap.medium.title

      return style
    },
    heroDescriptionStyle () {
      const style = {}
      if (this.menuSettings.hero_text_color) {
        style.color = this.menuSettings.hero_text_color
      }
      if (this.menuSettings.hero_font_family) {
        style.fontFamily = this.menuSettings.hero_font_family
      }

      // Font size for description
      const fontSize = this.menuSettings.hero_font_size || 'medium'
      const fontSizeMap = {
        small: '14px',
        medium: '16px',
        large: '18px',
        xlarge: '20px'
      }
      style.fontSize = fontSizeMap[fontSize] || fontSizeMap.medium

      return style
    },
    heroWrapperStyle () {
      const style = {
        transform: `translateY(${this.pullOffset}px)`,
        transition: this.isReleasing ? 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none',
        borderRadius: '20px 20px 0 0'
      }

      return style
    },
    heroOverlayBackgroundStyle () {
      const style = {}

      // Background color
      if (this.menuSettings.hero_transparent_background) {
        style.backgroundColor = 'transparent'
      } else if (this.menuSettings.hero_background_color) {
        style.backgroundColor = this.menuSettings.hero_background_color
      } else {
        style.backgroundColor = 'white'
      }

      return style
    },
    heroContentStyle () {
      const style = {}
      const textAlign = this.menuSettings.hero_text_align || 'center'
      style.textAlign = textAlign

      // Also set align-items for flexbox alignment
      if (textAlign === 'left') {
        style.alignItems = 'flex-start'
      } else if (textAlign === 'right') {
        style.alignItems = 'flex-end'
      } else {
        style.alignItems = 'center'
      }

      return style
    },
    categoryTabsStyle () {
      const style = {}

      // Background color - same as wrapper
      if (this.menuSettings.hero_transparent_background) {
        style.backgroundColor = 'transparent'
      } else if (this.menuSettings.hero_background_color) {
        style.backgroundColor = this.menuSettings.hero_background_color
      } else {
        style.backgroundColor = 'white'
      }

      // Apply same transform as content wrapper for pull effect
      style.transform = `translateY(${this.pullOffset}px)`
      style.transition = this.isReleasing ? 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none'

      return style
    },
    showLogo () {
      return this.menuSettings.show_logo !== false
    },
    itemsListStyle () {
      const style = {}
      // Apply same transform as content wrapper for pull effect
      style.transform = `translateY(${this.pullOffset}px)`
      style.transition = this.isReleasing ? 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none'
      return style
    },
    primaryColor () {
      // Get primary color or use default
      return this.menuSettings.items_primary_color || '#00b3be'
    },
    primaryColorRgb () {
      // Extract RGB values from hex color
      const hex = this.primaryColor.replace('#', '')
      const r = parseInt(hex.substring(0, 2), 16)
      const g = parseInt(hex.substring(2, 4), 16)
      const b = parseInt(hex.substring(4, 6), 16)
      return { r, g, b }
    },
    cssVarsStyle () {
      const rgb = this.primaryColorRgb
      return {
        '--primary-color': this.primaryColor,
        '--primary-color-rgb': `${rgb.r}, ${rgb.g}, ${rgb.b}`,
        '--primary-hover': `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.04)`,
        '--primary-hover-strong': `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.06)`
      }
    },
    itemsTextStyle () {
      const style = {}

      // Create gradient with primary color
      style.background = `linear-gradient(135deg, ${this.primaryColor} 0%, ${this.primaryColor} 50%, ${this.primaryColor} 100%)`
      style.webkitBackgroundClip = 'text'
      style.webkitTextFillColor = 'transparent'
      style.backgroundClip = 'text'

      if (this.menuSettings.items_font_family && this.menuSettings.items_font_family.trim() !== '') {
        style.fontFamily = this.menuSettings.items_font_family
      }
      return style
    },
    categoryHeaderStyle () {
      const rgb = this.primaryColorRgb

      return {
        background: `linear-gradient(135deg, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.08) 0%, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.06) 50%, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.08) 100%)`,
        borderBottomColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)`,
        backdropFilter: 'blur(10px)'
      }
    },
    itemRowHoverStyle () {
      const rgb = this.primaryColorRgb
      return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.04)`
    },
    itemImageWrapperStyle () {
      const rgb = this.primaryColorRgb
      return {
        background: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.05)`,
        boxShadow: `0 4px 12px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)`
      }
    },
    itemImagePlaceholderStyle () {
      const rgb = this.primaryColorRgb
      return {
        background: `linear-gradient(135deg, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.08) 0%, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.06) 50%, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.08) 100%)`
      }
    },
    categoryItemCountStyle () {
      const rgb = this.primaryColorRgb
      return {
        background: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.1)`,
        color: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.8)`
      }
    },
    iconColorStyle () {
      const rgb = this.primaryColorRgb
      return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.6)`
    },
    iconPlaceholderColorStyle () {
      const rgb = this.primaryColorRgb
      return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.3)`
    },
    filteredCategories () {
      if (!this.menuSettings.hide_empty_categories) {
        return this.categories
      }
      return this.categories.filter(cat =>
        this.getCategoryItemCount(cat.id) > 0
      )
    },
    cartItemCount () {
      return this.cart.reduce((sum, item) => sum + item.quantity, 0)
    },
    cartTotal () {
      return this.cart.reduce((sum, item) => {
        const price = this.getItemPrice(item)
        return sum + (price * item.quantity)
      }, 0)
    }
  },
  methods: {
    applyMenuData (data) {
      // Apply menu data from cache or fresh fetch
      this.companyId = data.company.id
      this.companyName = data.company.name || ''
      this.companyLogo = data.company.logo || ''
      this.locationName = data.location.name || ''
      this.locationAddress = `${data.location.address || ''}, ${data.location.city || ''}`.trim().replace(/^,\s*/, '')

      // Load menu settings - handle new template structure
      const savedSettings = data.location.menuSettings || {}
      const selectedTemplate = savedSettings.selected_template || 'aikasa'

      // If templates structure exists, use template-specific settings
      if (savedSettings.templates && savedSettings.templates[selectedTemplate]) {
        const templateSettings = savedSettings.templates[selectedTemplate]
        this.menuSettings = {
          ...this.menuSettings,
          ...templateSettings,
          // Also include common settings
          enable_ordering: savedSettings.enable_ordering,
          enable_call_waiter: savedSettings.enable_call_waiter,
          hide_inactive_items: savedSettings.hide_inactive_items,
          hide_empty_categories: savedSettings.hide_empty_categories
        }
      } else {
        // Fallback to old structure (for backward compatibility)
        this.menuSettings = { ...this.menuSettings, ...savedSettings }
      }

      this.categories = data.categories || []
      this.items = data.items || []
    },

    getCategoryItems (categoryId) {
      return this.items.filter(item =>
        item.category_ids && item.category_ids.includes(categoryId)
      )
    },

    getCategoryItemCount (categoryId) {
      return this.getCategoryItems(categoryId).length
    },

    showItemDetails (item) {
      this.selectedItem = item
      this.itemDetailsDialog = true
    },

    getItemCategoryName (item) {
      if (!item || !item.category_ids || item.category_ids.length === 0) {
        return ''
      }
      const categoryId = item.category_ids[0]
      const category = this.categories.find(cat => cat.id === categoryId)
      return category ? category.name : ''
    },

    getItemPrice (item) {
      if (!item || !item.prices) return 0

      const currency = this.menuSettings.default_currency || 'EUR'
      const priceData = item.prices[currency]

      if (!priceData || !priceData.active) return 0

      return priceData.price || 0
    },

    formatPrice (priceInCents) {
      if (!priceInCents) return '0,00 €'

      const currency = this.menuSettings.default_currency || 'EUR'
      const symbol = currency === 'EUR' ? '€' : 'kn'

      return (priceInCents / 100)
        .toFixed(2)
        .replace('.', ',') + ' ' + symbol
    },

    addToCart (item) {
      const existingItem = this.cart.find(cartItem => cartItem.id === item.id)

      if (existingItem) {
        existingItem.quantity++
      } else {
        this.cart.push({ ...item, quantity: 1 })
      }

      this.showSnackbar(`${item.name} dodano u košaricu`, 'success')
    },

    incrementQuantity (index) {
      this.cart[index].quantity++
    },

    decrementQuantity (index) {
      if (this.cart[index].quantity > 1) {
        this.cart[index].quantity--
      } else {
        this.removeFromCart(index)
      }
    },

    removeFromCart (index) {
      this.cart.splice(index, 1)
    },

    clearCart () {
      this.cart = []
      this.showSnackbar('Košarica ispražnjena', 'info')
    },

    openCart () {
      this.cartDialog = true
    },

    async submitOrder () {
      if (this.cart.length === 0) {
        this.showSnackbar('Košarica je prazna', 'warning')
        return
      }

      this.submittingOrder = true

      try {
        // Create order document
        const orderData = {
          location_id: this.locationId,
          company_id: this.companyId,
          items: this.cart.map(item => ({
            id: item.id,
            name: item.name,
            quantity: item.quantity,
            price: this.getItemPrice(item)
          })),
          total: this.cartTotal,
          currency: this.menuSettings.default_currency,
          status: 'PENDING',
          created_at: firebase.firestore.FieldValue.serverTimestamp(),
          order_type: 'QR_MENU'
        }

        await df.collection(`location_orders/${this.locationId}/orders`).add(orderData)

        this.showSnackbar('Narudžba uspješno poslana!', 'success')
        this.clearCart()
        this.cartDialog = false
      } catch (error) {
        console.error('Error submitting order:', error)
        this.showSnackbar('Greška pri slanju narudžbe', 'error')
      } finally {
        this.submittingOrder = false
      }
    },

    async callWaiter () {
      try {
        // Save waiter call to Firestore
        await df.collection(`waiter_calls/${this.locationId}/calls`).add({
          location_id: this.locationId,
          company_id: this.companyId,
          timestamp: firebase.firestore.FieldValue.serverTimestamp(),
          status: 'PENDING'
        })

        this.showSnackbar('Konobar je pozvan!', 'success')
      } catch (error) {
        console.error('Error calling waiter:', error)
        this.showSnackbar('Greška pri pozivu konobara', 'error')
      }
    },

    showSnackbar (text, color = 'success') {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    },

    truncateText (text, maxLength) {
      if (!text) return ''
      if (text.length <= maxLength) return text
      return text.substring(0, maxLength) + '...'
    },

    scrollToTop () {
      window.scrollTo({
        top: 0,
        behavior: 'auto'
      })
    },

    handleTouchStart (e) {
      // Only handle if at top of page
      if (window.pageYOffset === 0) {
        this.touchStartY = e.touches[0].clientY
        this.isPulling = true
      }
    },

    handleTouchMove (e) {
      if (!this.isPulling || window.pageYOffset > 0) {
        this.isPulling = false
        return
      }

      const touchY = e.touches[0].clientY
      const diff = touchY - this.touchStartY

      // Only allow pulling down (positive diff)
      if (diff > 0) {
        // Prevent default scrolling when pulling
        e.preventDefault()

        // Apply resistance - slower pull as it gets further
        const resistance = 0.2
        this.pullOffset = Math.min(diff * resistance, 40)
      }
    },

    handleTouchEnd () {
      if (this.isPulling) {
        this.isPulling = false
        this.isReleasing = true

        // Bounce back with animation
        setTimeout(() => {
          this.pullOffset = 0
          setTimeout(() => {
            this.isReleasing = false
          }, 500)
        }, 50)
      }
    },

    scrollToCategory (index) {
      const category = this.filteredCategories[index]
      if (!category) return

      const categorySection = this.$refs[`category-${category.id}`]
      if (categorySection && categorySection[0]) {
        const element = categorySection[0]
        const yOffset = -120 // Account for sticky headers (app bar + tabs)
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset

        window.scrollTo({ top: y, behavior: 'auto' })
      }
    },

    selectCategoryFromSheet (index) {
      this.categoriesSheet = false
      this.selectedCategoryIndex = index
      setTimeout(() => {
        this.scrollToCategory(index)
      }, 300)
    },

    handleScroll () {
      const scrollY = window.pageYOffset
      const heroHeight = 200 // Height of hero image

      // Show/hide app bar based on scroll position
      this.showAppBar = scrollY > heroHeight

      // Parallax effect - image moves down as you scroll down (revealing more)
      // Negative offset makes it look like we're "uncovering" the image
      if (scrollY <= heroHeight) {
        this.parallaxOffset = -scrollY * 0.5
      } else {
        this.parallaxOffset = -heroHeight * 0.5
      }

      // Debounce scroll handler
      if (this.scrollTimeout) clearTimeout(this.scrollTimeout)
      this.scrollTimeout = setTimeout(() => {
        this.updateActiveCategory()
      }, 50)
    },

    updateActiveCategory () {
      const scrollPosition = window.pageYOffset + 150 // Offset for sticky header

      // Find which category section is currently in view
      for (let i = this.filteredCategories.length - 1; i >= 0; i--) {
        const category = this.filteredCategories[i]
        const categorySection = this.$refs[`category-${category.id}`]

        if (categorySection && categorySection[0]) {
          const element = categorySection[0]
          const offsetTop = element.offsetTop

          if (scrollPosition >= offsetTop) {
            if (this.selectedCategoryIndex !== i) {
              this.selectedCategoryIndex = i
            }
            break
          }
        }
      }
    },

    expandSearch () {
      this.searchExpanded = true
    },

    clearSearch () {
      this.searchQuery = ''
      this.searchResults = []
      this.searchExpanded = false
    },

    filterSearchResults (query) {
      const searchLower = query.toLowerCase().trim()

      // Search categories
      const matchedCategories = this.filteredCategories.filter(category => {
        return category.name && category.name.toLowerCase().includes(searchLower)
      }).map(cat => ({
        ...cat,
        type: 'category'
      }))

      // Search items
      const matchedItems = this.items.filter(item => {
        const nameMatch = item.name && item.name.toLowerCase().includes(searchLower)
        const descMatch = item.description && item.description.toLowerCase().includes(searchLower)
        const allergenMatch = item.allergens && item.allergens.toLowerCase().includes(searchLower)

        return nameMatch || descMatch || allergenMatch
      }).map(item => ({
        ...item,
        type: 'item'
      }))

      // Combine results: categories first, then items
      this.searchResults = [...matchedCategories, ...matchedItems].slice(0, 10)
    },

    handleSearchResultClick (result) {
      if (result.type === 'category') {
        this.scrollToCategory(this.filteredCategories.findIndex(cat => cat.id === result.id))
      } else {
        this.scrollToItem(result.id)
      }
      // Close search after clicking
      this.clearSearch()
    },

    scrollToItem (itemId) {
      this.$nextTick(() => {
        const itemElement = document.querySelector(`[data-item-id="${itemId}"]`)
        if (itemElement) {
          // Account for app bar (56px) + category tabs (48px) + category section header (~65px) = ~170px
          const yOffset = -180
          const y = itemElement.getBoundingClientRect().top + window.pageYOffset + yOffset
          window.scrollTo({ top: y, behavior: 'auto' })

          // Highlight the item briefly
          itemElement.classList.add('highlight-item')
          setTimeout(() => {
            itemElement.classList.remove('highlight-item')
          }, 2000)
        }
      })
    }
  }
}
</script>

<style scoped>
/* Loading Screen Styles */
.menu-loading-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.menu-loading-logo {
  width: 200px;
  height: auto;
  margin-bottom: 40px;
  animation: menu-logo-pulse 2s ease-in-out infinite;
}

@keyframes menu-logo-pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(0.95);
  }
}

.menu-loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f0f0f0;
  border-top: 4px solid #00b3be;
  border-radius: 50%;
  animation: menu-spinner-spin 1s linear infinite;
}

@keyframes menu-spinner-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Main Container - Glassmorphism Background */
.menu-main {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
}

/* App Bar Custom Styling */
.app-bar-custom {
  cursor: pointer;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  transition: transform 0.3s ease, opacity 0.3s ease, box-shadow 0.3s ease;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.app-bar-custom.app-bar-hidden {
  transform: translateY(-100%);
  opacity: 0;
  pointer-events: none;
}

.app-bar-custom:hover {
  background-color: rgba(250, 250, 250, 1) !important;
}

.app-bar-logo-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
}

.app-bar-logo {
  border-radius: 4px;
}

.app-bar-title {
  font-size: 18px;
  font-weight: 700;
  color: #2c3e50;
  text-transform: lowercase;
  letter-spacing: 0.3px;
}

.app-bar-title::first-letter {
  text-transform: uppercase;
}

/* Hero Section */
.hero-section {
  position: relative;
  width: 100%;
  overflow: visible;
}

.hero-content-wrapper {
  position: relative;
  will-change: transform;
  z-index: 10;
  overflow: visible;
  min-height: 200px;
}

.hero-image-container-full {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1;
  overflow: hidden;
  height: 200px;
}

.hero-image-wrapper-full {
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform-origin: center top;
  will-change: transform;
}

.hero-background-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  top: 110px;
  z-index: 1;
  border-radius: 20px 20px 0 0;
}

.hero-content-overlay {
  position: relative;
  z-index: 2;
  padding: 120px 16px 8px 16px;
}

.hero-content-overlay > div {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.hero-logo-wrapper {
  background: white;
  border-radius: 50%;
  padding: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  margin-bottom: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 3px solid #f5f7fa;
}

.hero-logo {
  border-radius: 8px;
}

.hero-logo-icon {
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.1));
}

.hero-title {
  font-size: 28px;
  font-weight: 700;
  color: white;
  margin: 0 0 8px 0;
  text-transform: lowercase;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.6);
}

.hero-title::first-letter {
  text-transform: uppercase;
}

.hero-description {
  color: white;
  margin: 0;
  max-width: 600px;
  line-height: 1.5;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
}

/* Description font sizes based on settings */
.hero-content-overlay .hero-description {
  font-size: 16px;
}

/* Category Tabs Container - Sticky with button */
.category-tabs-container {
  position: sticky;
  top: 56px;
  z-index: 9;
  display: flex;
  align-items: stretch;
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  border-bottom: 2px solid rgba(0, 179, 190, 0.25) !important;
  box-shadow: 0 2px 12px rgba(0, 179, 190, 0.15) !important;
  min-height: 48px;
  height: 48px;
  margin-top: 0;
  padding-top: 0;
}

/* Category Tabs Wrapper - Scrollable */
.category-tabs-wrapper {
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}

/* Hide scrollbar but keep scrolling */
.category-tabs-wrapper::-webkit-scrollbar {
  display: none;
}

.category-tabs-wrapper {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.category-tabs {
  box-shadow: none !important;
  background: transparent !important;
}

.category-tabs >>> .v-tabs-slider {
  display: none !important;
}

.category-tabs >>> .v-slide-group__wrapper {
  overflow-x: auto !important;
  padding-left: 0 !important;
  margin-left: 0 !important;
}

.category-tabs >>> .v-slide-group__content {
  justify-content: flex-start !important;
  padding-left: 0 !important;
  margin-left: 0 !important;
}

.category-tabs >>> .v-slide-group__prev,
.category-tabs >>> .v-slide-group__next {
  display: none !important;
  width: 0 !important;
  min-width: 0 !important;
  max-width: 0 !important;
  flex: 0 0 0 !important;
  padding: 0 !important;
  margin: 0 !important;
  overflow: hidden !important;
}

.category-tab {
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
  font-size: 14px !important;
  min-width: auto !important;
  padding: 8px 20px !important;
  margin: 6px 4px !important;
  border-radius: 24px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  background: transparent !important;
  color: rgba(0, 0, 0, 0.7) !important;
}

.category-name-text {
  text-transform: lowercase !important;
}

.category-name-text::first-letter {
  text-transform: uppercase !important;
}

.category-tab:first-child {
  margin-left: 8px !important;
}

.category-tab:hover {
  background: var(--primary-hover-strong) !important;
  transform: translateY(-1px);
}

/* Active tab - oval balloon style */
.category-tab.v-tab--active {
  background: white !important;
  color: var(--primary-color) !important;
  border: 2px solid var(--primary-color) !important;
  box-shadow: 0 4px 12px rgba(var(--primary-color-rgb), 0.25) !important;
  transform: translateY(-2px);
  font-weight: 700 !important;
}

.category-tab.v-tab--active .v-icon {
  color: var(--primary-color) !important;
}

/* Categories Menu Button - Sticky Right */
.categories-menu-btn {
  border: none !important;
  box-shadow: -4px 0 12px rgba(var(--primary-color-rgb), 0.25) !important;
  flex-shrink: 0 !important;
  width: 56px !important;
  height: 100% !important;
  border-radius: 12px 0 0 12px !important;
  margin: 0 !important;
  align-self: stretch !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.categories-menu-btn:hover {
  box-shadow: -6px 0 20px rgba(var(--primary-color-rgb), 0.3) !important;
  filter: brightness(0.95);
}

/* Categories Sheet */
.categories-sheet-card {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  max-height: 70vh;
  overflow-y: auto;
  border-radius: 16px 16px 0 0 !important;
}

.categories-sheet-card .v-card__title {
  background: var(--primary-hover) !important;
  font-weight: 700 !important;
  border-radius: 16px 16px 0 0 !important;
}

/* Item Details Bottom Sheet */
.item-details-sheet-card {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  height: 75vh;
  overflow-y: auto;
  border-radius: 16px 16px 0 0 !important;
}

.item-detail-header {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px;
  background: linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 100%);
  z-index: 1;
}

.item-detail-header .v-card__title {
  flex: 1;
  padding: 0;
}

.item-detail-header .close-btn {
  background: rgba(0, 0, 0, 0.3) !important;
}

.item-detail-header-no-image {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--primary-hover) !important;
  border-radius: 16px 16px 0 0 !important;
  padding: 8px 16px;
}

.item-detail-header-no-image .v-card__title {
  padding: 0;
  flex: 1;
}

.categories-sheet-card .v-list-item {
  border-radius: 8px !important;
  margin: 4px 8px !important;
  transition: all 0.2s ease !important;
}

.categories-sheet-card .v-list-item:hover {
  background: var(--primary-hover) !important;
}

.categories-sheet-card .v-list-item--active {
  background: var(--primary-hover-strong) !important;
  border: 2px solid var(--primary-color) !important;
}

.categories-sheet-card .v-list-item__title {
  font-weight: 600 !important;
  text-transform: lowercase !important;
}

.categories-sheet-card .v-list-item__title::first-letter {
  text-transform: uppercase !important;
}

.categories-sheet-card .v-list-item__subtitle {
  color: rgba(120, 120, 130, 0.7) !important;
}

/* Items List Container - Negative margin to pull up */
.items-list-container {
  margin-top: -48px !important;
}

/* Items Container - Glass Card */
.items-container {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px) !important;
  border-radius: 0 0 16px 16px;
  box-shadow: 0 8px 32px rgba(0, 179, 190, 0.1) !important;
  margin-top: 0;
  padding-top: 48px;
}

/* Category Section */
.category-section {
  scroll-margin-top: 120px; /* Space for sticky headers */
}

/* Category Section Header */
.category-section-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom-width: 2px;
  border-bottom-style: solid;
  position: sticky;
  top: 104px; /* Below app bar (56px) and category tabs container (48px) */
  z-index: 8;
}

.category-section-title {
  font-size: 18px !important;
  font-weight: 700 !important;
  letter-spacing: 0.5px !important;
  display: inline-block !important;
  flex: 1;
  text-transform: lowercase !important;
}

.category-section-title::first-letter {
  text-transform: uppercase !important;
}

.category-item-count {
  font-size: 14px;
  font-weight: 500;
  padding: 4px 12px;
  border-radius: 12px;
}

/* Item Row */
.item-row {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  position: relative;
}

.item-row.item-clickable {
  cursor: pointer;
}

.item-row.item-clickable:hover {
  background: var(--primary-hover) !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.item-content {
  display: flex;
  gap: 12px;
  padding: 16px;
  align-items: flex-start;
}

/* Item Image Wrapper - Left Side */
.item-image-wrapper {
  flex-shrink: 0;
  width: 100px;
  height: 100px;
  border-radius: 12px;
  overflow: hidden;
  background: rgba(0, 179, 190, 0.05);
  box-shadow: 0 4px 12px rgba(0, 179, 190, 0.15);
}

.item-image {
  width: 100px !important;
  height: 100px !important;
  object-fit: cover;
}

.item-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.08) 0%,
    rgba(57, 137, 201, 0.06) 50%,
    rgba(97, 84, 164, 0.08) 100%);
}

/* Item Info - Right Side */
.item-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* Item Header - Name and Price on Same Line */
.item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.item-name {
  font-size: 16px !important;
  font-weight: 700 !important;
  line-height: 1.3;
  letter-spacing: 0.3px !important;
  flex: 1;
  text-transform: lowercase !important;
}

.item-name::first-letter {
  text-transform: uppercase !important;
}

.item-price {
  font-size: 16px !important;
  font-weight: 700 !important;
  letter-spacing: 0.5px !important;
  display: inline-block !important;
  white-space: nowrap;
}

.item-description {
  font-size: 13px !important;
  color: rgba(120, 120, 130, 0.9) !important;
  line-height: 1.5;
  letter-spacing: 0.2px;
  margin-top: 2px;
}

.item-allergens {
  font-size: 12px !important;
  color: #f59e0b !important;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  font-weight: 500;
}

/* Add Button Floating - Bottom Right */
.add-button-floating {
  position: absolute !important;
  bottom: 16px !important;
  right: 16px !important;
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.95) 0%,
    rgba(57, 137, 201, 0.9) 50%,
    rgba(97, 84, 164, 0.95) 100%) !important;
  border: 2px solid rgba(255, 255, 255, 0.4) !important;
  box-shadow: 0 4px 12px rgba(0, 179, 190, 0.3) !important;
  backdrop-filter: blur(10px) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.add-button-floating:hover {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 1) 0%,
    rgba(57, 137, 201, 0.95) 50%,
    rgba(97, 84, 164, 1) 100%) !important;
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(0, 179, 190, 0.5) !important;
}

.add-button-floating .v-icon {
  color: white !important;
}

/* Item Divider */
.item-divider {
  border-color: rgba(0, 179, 190, 0.1) !important;
}

/* Call Waiter Button - Glass FAB */
.call-waiter-btn {
  bottom: 24px !important;
  right: 24px !important;
  background: linear-gradient(135deg,
    rgba(255, 152, 0, 0.95) 0%,
    rgba(255, 193, 7, 0.9) 50%,
    rgba(255, 152, 0, 0.95) 100%) !important;
  border: 2px solid rgba(255, 255, 255, 0.4) !important;
  box-shadow: 0 8px 24px rgba(255, 152, 0, 0.35) !important;
  backdrop-filter: blur(10px) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.call-waiter-btn:hover {
  background: linear-gradient(135deg,
    rgba(255, 152, 0, 1) 0%,
    rgba(255, 193, 7, 1) 50%,
    rgba(255, 152, 0, 1) 100%) !important;
  transform: scale(1.08);
  box-shadow: 0 12px 32px rgba(255, 152, 0, 0.5) !important;
}

/* Error Container */
.error-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.empty-state {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px) !important;
  margin: 16px;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 179, 190, 0.1) !important;
}

/* Mobile Optimizations */
@media (max-width: 600px) {
  .call-waiter-btn {
    bottom: 16px !important;
    right: 16px !important;
  }

  .item-content {
    padding: 12px;
    gap: 10px;
  }

  .item-image-wrapper {
    width: 80px;
    height: 80px;
  }

  .item-image {
    width: 80px !important;
    height: 80px !important;
  }

  .item-name {
    font-size: 15px !important;
  }

  .item-description {
    font-size: 12px !important;
  }

  .item-price {
    font-size: 15px !important;
  }

  .add-button-floating {
    bottom: 12px !important;
    right: 12px !important;
  }

  .category-tabs-wrapper {
    top: 48px;
  }
}

/* Floating Search Bar */
.floating-search-bar {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 100;
  display: flex;
  align-items: flex-end;
  gap: 12px;
}

.search-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.search-results-wrapper {
  margin-bottom: 8px;
  width: 280px;
}

.search-fab {
  box-shadow: 0 4px 16px rgba(var(--primary-color-rgb), 0.25) !important;
}

.search-fab:hover {
  box-shadow: 0 6px 24px rgba(var(--primary-color-rgb), 0.3) !important;
  filter: brightness(0.95);
}

.search-input {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px) !important;
  border-radius: 24px !important;
  width: 280px;
  box-shadow: 0 4px 16px rgba(0, 179, 190, 0.2) !important;
}

.search-input .v-input__control {
  border-radius: 24px !important;
}

.search-input .v-text-field__details {
  display: none;
}

/* Search Results Card */
.search-results-card {
  background: white !important;
  border-radius: 12px !important;
  max-height: 400px;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 179, 190, 0.25) !important;
}

.search-result-item {
  cursor: pointer;
  transition: background 0.2s ease;
}

.search-result-item:hover {
  background: rgba(0, 179, 190, 0.05) !important;
}

.search-result-name {
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  text-transform: lowercase;
}

.search-result-name::first-letter {
  text-transform: uppercase;
}

.search-result-price {
  font-weight: 700;
  color: #00b3be;
  font-size: 14px;
}

/* Highlight effect for search results */
.highlight-item {
  animation: highlightPulse 2s ease-in-out;
}

@keyframes highlightPulse {
  0%, 100% {
    background: transparent;
  }
  50% {
    background: linear-gradient(135deg,
      rgba(0, 179, 190, 0.15) 0%,
      rgba(57, 137, 201, 0.1) 50%,
      rgba(97, 84, 164, 0.15) 100%);
  }
}
/* Item Details Modal */
.item-detail-title {
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
  padding: 20px !important;
}
</style>
